import { Sparkles, BarChart3, TrendingUp, Activity, Zap, Users, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnalyticsDashboard, MetricsCard } from '../components/analytics';
import NotificationCenter from '../components/dashboard/NotificationCenter';
import UserMenu from '../components/dashboard/UserMenu';
import { useAppStore } from '../store/appStore';

export default function Analytics() {
  const navigate = useNavigate();
  const {
    aiInteractionsToday,
    activeSessions,
    dailyInteractions,
    appUsageByCategory,
    engagementMetrics
  } = useAppStore();

  // Calculate metrics for cards
  const totalApps = appUsageByCategory.reduce((sum, cat) => sum + cat.count, 0);
  const totalUsers = engagementMetrics.reduce((sum, metric) => sum + metric.value, 0);
  const avgInteractions = Math.round(
    dailyInteractions.reduce((sum, val) => sum + val, 0) / dailyInteractions.length
  );

  // Calculate change percentages
  const interactionsChange = dailyInteractions.length >= 2
    ? Math.round(
        ((dailyInteractions[dailyInteractions.length - 1] - dailyInteractions[dailyInteractions.length - 2]) /
          dailyInteractions[dailyInteractions.length - 2]) *
          100
      )
    : 0;

  const lastWeekData = dailyInteractions.slice(-7);

  return (
    <div className="min-h-screen bg-recipe-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-recipe-card border-b border-recipe-border backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="bg-recipe-accent rounded-lg p-2 hover:bg-recipe-accent/90 transition-colors"
              >
                <Sparkles className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-recipe-text">Analytics Dashboard</h1>
                <p className="text-xs text-recipe-text-muted hidden sm:block">
                  Real-time insights and performance metrics
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm text-recipe-text-muted hover:text-recipe-text transition-colors"
              >
                Back to Dashboard
              </button>

              {/* Notification Center */}
              <NotificationCenter />

              {/* User Menu */}
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-recipe-accent" />
            <h2 className="text-3xl font-bold text-recipe-text">Analytics Overview</h2>
          </div>
          <p className="text-recipe-text-muted">
            Track your workspace performance, user engagement, and system metrics in real-time
          </p>
        </div>

        {/* Metrics Cards Grid */}
        <section className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricsCard
              title="AI Interactions"
              value={aiInteractionsToday}
              change={interactionsChange}
              icon={<Zap className="w-5 h-5" />}
              sparklineData={lastWeekData}
            />
            <MetricsCard
              title="Active Sessions"
              value={activeSessions}
              icon={<Activity className="w-5 h-5" />}
              trend="up"
            />
            <MetricsCard
              title="Total Users"
              value={totalUsers}
              change={12}
              icon={<Users className="w-5 h-5" />}
            />
            <MetricsCard
              title="Avg. Daily Use"
              value={avgInteractions}
              icon={<Clock className="w-5 h-5" />}
              sparklineData={dailyInteractions}
            />
          </div>
        </section>

        {/* Additional Info Cards */}
        <section className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm font-semibold text-recipe-text">System Status</span>
              </div>
              <p className="text-xs text-recipe-text-muted">All systems operational</p>
            </div>

            <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-recipe-accent" />
                <span className="text-sm font-semibold text-recipe-text">Growth Rate</span>
              </div>
              <p className="text-xs text-recipe-text-muted">+23% from last week</p>
            </div>

            <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-recipe-accent" />
                <span className="text-sm font-semibold text-recipe-text">Total App Uses</span>
              </div>
              <p className="text-xs text-recipe-text-muted">{totalApps.toLocaleString()} this month</p>
            </div>
          </div>
        </section>

        {/* Charts Dashboard */}
        <section>
          <AnalyticsDashboard />
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-recipe-border mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-recipe-text-muted text-sm">
              Recipe Labs Hub v1.0.0 - Analytics Dashboard
            </p>
            <div className="flex items-center gap-4 text-sm text-recipe-text-muted">
              <button
                onClick={() => navigate('/settings')}
                className="hover:text-recipe-accent transition-colors duration-200"
              >
                Settings
              </button>
              <span>•</span>
              <button
                onClick={() => navigate('/')}
                className="hover:text-recipe-accent transition-colors duration-200"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
